<template>
    <div    class="px-2 py-1 my-1 w-full">
        <select-series-modal    v-model:show-modal="selectingSeries"
                                :points="seriesList"
                                :scaling-profiles="scalingProfiles"
                                v-model:error-multiple-scaling-profiles="errors['errorAddingSeriesTooManyScalingProfiles']"
                                v-model:series-visible="seriesToShow"
        ></select-series-modal>

        <div    v-if="loadingPageStatus === loadingStatus.meta">
            <div class="flex flex-row items-center justify-center py-4">
                <span :class="iconLoading"></span>
                <span class="text-grey-500 text-base font-medium">Loading Point Information, please wait.</span>
            </div>
        </div>

        <div    v-else-if="loadingPageStatus === loadingStatus.data"
                      class="flex flex-row items-center justify-center py-4">
            <span :class="iconLoading"></span>
            <span class="text-grey-500 text-base font-medium">Loading Data on Graph.</span>
        </div>

        <div    v-if="loadingPageStatus === loadingStatus.complete && numData() === 0"
                class="text-md">
            <p>
                No data found in specified range.
            </p>
            <p>
                Last Check-in was {{ prettyLastUpdateTime }}.
            </p>
        </div>

        <div    class="w-full flex">
            <div    class="w-1/3"></div>
            <div    class="w-1/3 justify-self-center">
                <div    v-if="errorMessage !== ''">
                    <banner-error>
                        <template   #title>Error Exporting Points</template>
                        <template   #content>{{ errorMessage }}</template>
                    </banner-error>
                </div>
            </div>

            <div    class="w-1/3 justify-self-end flex flex-row">
                <!-- Extra Settings on Local, Staging, & QA -->
                <div    class="mb-1 px-2 w-full flex justify-end">
                    <div    v-if="! isHeavyLoad() && showBackFillToggle()">
                        <tooltip-container    :width="300">
                            <!-- Tooltip for back-fill -->
                            <template   #tooltip-text>
                                Enabling this feature highlights readings in which Sonicu's DataSync technology
                                has ensured all of your data made it to SoniCloud, protecting you against disruptions
                                in connectivity.
                            </template>

                            <!-- Show Back-fill Toggle -->
                            <div    class="flex flex-col items-center mr-4 rounded-md border p-3 py-2">
                                <span>Highlight Data Sync</span>
                                <div>
                                    <toggle     id="show_back_fill_windows"
                                                v-model:value="loadBackFillWindows"
                                                :togglable="true"
                                                name="show_back_fill_windows"
                                    ></toggle>
                                </div>
                            </div>
                        </tooltip-container>
                    </div>

                    <!-- Number of Points -->
                    <div    v-if="this.numData() > 0 && showQaTestData()"
                            class="flex flex-col items-center mr-4 rounded-md border p-3 py-2">
                        <span>Number of Points</span>
                        <span>{{ numData() }}</span>
                    </div>

                    <!-- Timezone -->
                    <div    v-if="this.numData() > 0 && showQaTestData()"
                            class="py-2 px-4 border-solid border-gray-900 bg-blue-500 text-white rounded-md flex items-center">
                        <div    class="flex items-center">
                            <small-colored-check
                                v-model="useUTC"
                                :togglable="true"
                            ></small-colored-check>
                            <span   class="lnr icon-earth"></span>
                        </div>
                        <span   class="ml-2">{{ timezone }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div
                class="relative border-gray-300 rounded-md"
                :class="[pointsLoaded() ? 'border border-solid' : '']"
                :style="graphHeight">
            <div    v-show="numData() === 0"
                    class="absolute bg-white w-full"
                    :class="[loadingPageStatus === loadingStatus.complete && numData() === 0 ? 'z-50' : '']"
                    :style="graphHeight"></div>
            <div    class="absolute w-full"
                    id="meter-graph"
                    ref="meterGraph"
                    :style="graphHeight"
            ></div>
        </div>
    </div>
</template>

<script>

import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary";

import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from "@/mixins/IconNamesMixin"

import {am4ChartMixin} from "@/mixins/am4charts/am4ChartMixin";
import {am4ScrollbarXMixin} from "@/mixins/am4charts/am4ScrollbarXMixin";
import {am4LegendMixin} from "@/mixins/am4charts/am4LegendMixin";
import {am4Settings} from "@/mixins/am4charts/am4Settings";
import {SeriesColor} from "@/mixins/am4charts/SeriesColor"

import axios from "axios"
import _ from "lodash"

// AM4 Charts
import {amchartsMixin} from "@/mixins/amchartsMixin";
import * as am4maps from "@amcharts/amcharts4/maps";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import SmallColoredCheck from "@/components/elements/SmallColoredCheck";
import {Series} from "@/mixins/am4charts/series"
import Select2 from "@/components/elements/select2";
import {SideAxis} from "@/mixins/am4charts/SideAxis"
import {DateAxis} from "@/mixins/am4charts/DateAxis"
import SelectSeriesModal from "@/components/SelectSeriesModal";
import BannerError from "./Banners/BannerError.vue";
import TooltipContainer from "./elements/TooltipContainer.vue";
import Toggle from "./elements/toggle.vue";



const LOADING_POINT_META = 0
const LOADING_POINT_DATA = 1
const LOADING_POINT_DATA_COMPLETE = 3

const MAX_POINTS_PER_REQUEST = 500;


export default {
    name: "MeterGraphFull",
    mixins: [SonicuCoreMixin, iconNamesMixin,
        amchartsMixin, am4ChartMixin, am4ScrollbarXMixin,
        am4LegendMixin
    ],
    components: {
        Toggle,
        TooltipContainer, BannerError, SelectSeriesModal, Select2, SmallColoredCheck, ButtonBlueSecondary},
    props: {
        id: Number,
        name: String,
        useShortGaps: Boolean,
        startDate: {
            type: Date,
            default() {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                return yesterday;
            },
        },
        endDate: {
            type: Date,
            default() {
                return new Date()
            }
        },
        fileName: String,
        timezone: String,
        filterDataReady: {
            type: Boolean,
            default: true,
        },
        pairPointOptions: {},
        showPairedPoint: Boolean,
        pairedPoints: {
            type: [Array, Object],
            default() {
                return {}
            }
        },
        pointIds: Array,
        showSeriesModal: Boolean,
        username: String,
        validationReadings: {
            type: Array,
            default: null,
        },
    },
    data() {
        return {
            chartReady: false,
            useUTC: false,
            selectingSeries: false,
            loadBackFillWindows: false,

            loadFunctionTmp: null,
            updateGraphDebounce: null,

            scalingProfiles: [],
            seriesToShow: [`${this.id}`],

            lastUpdateTime: null,
            requestNumber: 0,
            seriesSummaryPageNumber: 0,

            formatter: null,
            companyTimezone: null,
            legend: null,

            loadingStatus: {
                meta: 0,
                data: 1,
                complete: 2,
            },
            numPointPages: 0,
            pointPagesLoaded: 0,
            loadingPageStatus: 0,
            isLoadScalingProfileComplete: false,
            isLoadSeriesSummaryDataComplete: false,

            errors: {
                errorAddingSeriesTooManyScalingProfiles: false,
            },

            // New version of Y-Axis
            renderedScalingProfiles: [],
            yAxis: [],
            chartTitle: '',
            errorMessage: '',
        }
    },
    created() {
        // This is required by the Amcharts/Vuejs integration
        // https://www.amcharts.com/docs/v4/getting-started/integrations/using-vue-js/#selenium-highlight)
        this.chart = null
        this.seriesList = {}
        this.leftAxis = null
        this.dateAxis = null

        // Now you can utilize the rest of the objects like normal

        this.initializeAmchartsNoAnimation()
        SeriesColor.resetColors()

        // Add the Y-Axis
        this.leftAxis = new SideAxis()
        this.yAxis = [this.leftAxis]
        this.dateAxis = new DateAxis(this.startDate, this.endDate, this.timezone)
        this.useUTC = false
        this.formatter = new am4core.DateFormatter()
        this.formatter.timezone = this.timezone
        am4Settings.dateFormatter = this.formatter
        am4Settings.updateVueHook = _.debounce(event => {
            if (this.loadingPageStatus === this.loadingStatus.data && !this.isLoading()) {
                this.loadingPageStatus = this.loadingStatus.complete
            }
            this.$forceUpdate()
        }, 100)

        this.loadScalingProfileData()

        this.requestNumber++
        this.selectingSeries = false
    },
    mounted() {
        this.chart = this.am4CreateChart(this.$refs.meterGraph, am4charts.XYChart)
        this.chart.visible = false

        // Axis
        this.leftAxis.buildAndRender(this.chart)
        this.leftAxis.renderLeft()
        this.dateAxis.buildAndRender(this.chart)
        this.loadSeriesSummaryData()

        // If the user is sonicu or this is qa/staging/local, show the back-fill toggle
        if (this.permissions.isSonicu() || (this.environment.isQa() || this.environment.isLocal() || this.environment.isStaging())) {
            this.loadBackFillWindows = true
        }
    },
    methods: {
        loadScalingProfileData() {
            console.time('MeterGraphFull@loadScalingProfileData')
            axios.get(this.company.buildCompanyApiInternalRoute('current-profile'), {
                params: {}
            }).then(response => {
                this.scalingProfiles = response.data
                console.timeEnd('MeterGraphFull@loadScalingProfileData')
            }).then(() => {
                this.isLoadScalingProfileComplete = true
                if (this.isLoadSeriesSummaryDataComplete) {
                    this.chart.visible = true
                }
            })
        },
        loadSeriesSummaryData() {
            // Set loading status to meta, because we are loading meta-data
            this.loadingPageStatus = this.loadingStatus.meta

            // Get the number of pages of seriesSummaries to load
            //      Number of points can come from MeterView@pointIds (which is a property here)
            //      Must make sure that it is loaded though.
            let numPages = _.size(this.pointIds) / MAX_POINTS_PER_REQUEST
            this.numPointPages = _.ceil(numPages)
            this.pointPagesLoaded = 0

            // For each of the pages, load the data
            _.each(_.range(this.numPointPages), pageNumber => {
                this.loadNextSeriesSummaryDataPage(pageNumber)
            })
        },
        loadNextSeriesSummaryDataPage(pageNumber) {
            axios.get(this.company.buildCompanyApiInternalRoute('graph-meter') + '/' + this.id, {
                params: {
                    requestNumber: this.requestNumber,
                    page: pageNumber,
                }
            }).then(response => {
                if (parseInt(response.data.requestNumber) !== this.requestNumber) {
                    return
                }
                let data = response.data.pointData
                let mainSeriesFound = false
                _.each(data, (point, id) => {
                        let seriesData = {
                            id: id,
                            name: point.name,
                            serialNumber: point.serialNumber,
                            requestNumber: point.requestNumber,
                            alarmProfileId: point.alarmProfileId,
                            scalingProfileId: point.scalingProfileId,
                            alarmInfo: point.alarmInfo,
                            lastUpdateTime: point.lastUpdateTime
                        }
                        if (!_.has(this.seriesList, id)) {
                            let tmpScalingProfile = null;
                            if(_.has(this.scalingProfiles, point.scalingProfileId)){
                                tmpScalingProfile = this.scalingProfiles[point.scalingProfileId]
                            }
                            this.seriesList[id] = new Series(this.company.getCompanyId(), this.chart, id, this.leftAxis, tmpScalingProfile, this.startDate, this.endDate, am4Settings.AXIS_LEFT, this.loadBackFillWindows)
                        }
                        let s = this.seriesList[id]
                        s.updateFromSeriesData(seriesData)
                        console.log(`[MeterGraphFull] Setting validation readings for series ${id}:`, this.validationReadings)
                        s.setValidationReadings(this.validationReadings)
                        s.setDateAxis(this.dateAxis)

                        if (id === `${this.id}` && this.leftAxis.series.length === 0) {
                            this.leftAxis.addSeries(s)
                            if (_.has(this.scalingProfiles, s.scalingProfileId)) {
                                this.leftAxis.setCurrentProfile(this.scalingProfiles[s.scalingProfileId])
                                this.renderedScalingProfiles.push(this.scalingProfiles[s.scalingProfileId])
                                mainSeriesFound = true
                            }
                            this.lastUpdateTime = point.lastUpdateTime
                        }
                    }
                )
                if (_.isNil(response.data.nextPage)) {
                    this.seriesSummaryPageNumber = 0
                }

                if (mainSeriesFound) {
                    this.updateChart()
                }
            })
        },
        updateChart() {
            if (!this.filterDataReady) {
                return false
            }

            // If the update method is setup call it; otherwise set it up.
            if (!_.isNil(this.updateGraphDebounce)) {
                let updateGraphCall = new Promise(() => this.updateGraphDebounce())
                return true
            }

            else {
                // Define Debounce Graph once
                // Method to update Graph later
                this.updateGraphDebounce = _.debounce(() => {
                    console.log(`Updating Graph`)
                    if (!this.chartReady) {
                        if (Object.keys(this.seriesList).indexOf(`${this.id}`) !== -1) {
                            this.setupChart()
                        }
                        else {
                            return
                        }
                    }

                    // Do Reset things
                    this.dateAxis.setSeries(this.seriesList)

                    // No Update Time intervals for all series.
                    this.dateAxis.updateTimeIntervals(this.startDate, this.endDate)
                    this.formatter.timezone = this.timezone

                    if (this.legend) {
                        this.legend.dispose()
                    }

                    this.setupLegend()
                    this.setupScrollbar()


                    // Update Min Max Intervals for Axis
                    _.each(this.yAxis, axis => {
                        axis.updateMinMax()
                    })

                    this.dateAxis.updateGapAndMinBulletDistance()
                }, 500)

                // With the update debounce method defined, return update chart (Max recursion depth 1)
                return this.updateChart()
            }
        },
        setupChart() {
            // Chart Info
            this.chartReady = false
            this.legend = null

            // Chart Configuration & plugins
            this.setupChartConfiguration()
            this.am4SetupChartPlugins(this.chart)
            this.am4SetupChartTitle(this.chart)

            this.chartReady = true
        },
        setupChartConfiguration() {
            this.am4SetupChartConfiguration(this.chart, this.formatter, am4Settings.RELOAD_FREQUENCY)
        },
        setupScrollbar() {
            let info = this.am4SetupScrollbar(this.chart, this.isHeavyLoad(), am4Settings.dateFormatter)
            if (!this.isHeavyLoad() && !_.isNil(info.scrollBar)) {
                _.forEach(this.seriesList, series => {
                    if (series.visible && this.loadBackFillWindows) {
                        series.renderBackFillIntervals()
                    }
                })
            }
        },
        setupLegend() {
            if (!_.isNil(this.legend)) {
                this.legend.visible = false
                this.legend.parent = null
                this.legend.dispose()
                this.legend = null
            }

            if (! this.isHeavyLoad()) {
                return null
            }


            let legend = new am4maps.Legend()
            legend.parent = this.chart.chartContainer
            legend.align = 'center'
            legend.padding(10, 15, 10, 15)
            legend.visible = true
            legend.data = []

            // Get Series on Chart
            let series = []
            _.each(this.yAxis, axis => {
                series = _.concat(series, axis.series)
            })

            // Update Legend Information with series
            _.each(series, s => {
                legend.data.push({
                    name: s.name,
                    id: s.id,
                    fill: s.color,
                })
            })

            // Make sure we can show and hide series
            legend.itemContainers.template.events.on("hit", ev => {
                let id = ev.target.dataItem.dataContext.id
                let series = this.seriesList[id]
                if (series.visible) {
                    series.hide()
                }
                else {
                    series.show()
                }
            });

            this.legend = legend
        },
        addSeriesToChart(point) {
            if (_.isNil(point)) {
                return
            }

            this.errors.errorAddingSeriesTooManyScalingProfiles = false


            // Check if the scaling profile is the same as the left axis
            if (this.leftAxis.checkScalingProfile(point.scalingProfileId)) {
                this.leftAxis.addSeries(point, true)
            }
            else {
                let pointAdded = false

                // Determine the correct axis to add the scaling profile to.
                _.forEach(this.renderedScalingProfiles, (profile, index) => {
                    if (profile.id === point.scalingProfileId) {
                        this.yAxis[index].addSeries(point, true)
                        pointAdded = true
                    }
                });

                if (! pointAdded) {
                    let scalingProfileIndex = null

                    // Check all scaling profiles and render the correct one
                    _.forEach(this.scalingProfiles, (profile, index) => {
                        if (profile.id === point.scalingProfileId) {
                            this.renderedScalingProfiles.push(profile)
                            let sideAxis = new SideAxis()
                            sideAxis.setCurrentProfile(profile)
                            sideAxis.buildAndRender(this.chart)
                            sideAxis.renderRight()

                            this.yAxis.push(sideAxis)
                            scalingProfileIndex = index
                        }
                    });

                    // Add the point to the scaling profile if it is found
                    if (!_.isNil(scalingProfileIndex)) {
                        let lastAxisLength = this.yAxis.length - 1
                        this.yAxis[lastAxisLength].addSeries(point, true)
                    }
                }
            }

            return true
        },
        numData() {
            let count = 0
            _.forEach(this.seriesList, item => {
                if (item.isAm4SeriesReady()) {
                    count += item.am4Series.data.length
                }
            })
            return count
        },
        isLoading() {
            let isLoading = false
            _.forEach(this.seriesList, series => {
                if (series.visible && series.isLoadingData()) {
                    isLoading = true
                }
            })

            return isLoading
        },
        exportData() {
            this.errorMessage = ''
            axios.get(this.company.buildCompanyApiInternalRoute('data-meter-export'), {
                responseType: 'blob',
                params: {
                    start: this.startDate,
                    end: this.endDate,
                    useUTC: this.useUTC,
                    ids: this.seriesToShow,
                },
            }).then(response => {
                const url = window.URL.createObjectURL(new Blob([
                    response.data
                ]))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', 'graphExport.csv')
                document.body.appendChild(link)
                link.click()
            }).catch((error, data) => {
                const errorMessage = new Blob([
                    error.response.data
                ], {type: 'application/json'})
                errorMessage.text().then((text) => {
                    this.errorMessage = (JSON.parse(text))['data']
                    setTimeout(() => {
                        this.errorMessage = ''
                    }, 10000)
                })
            })
        },
        pointsLoaded() {
            let pointsLoaded = this.loadingPageStatus === this.loadingStatus.complete
                && this.numData() > 0

            this.$emit('hasSeriesForModal', pointsLoaded)
            return pointsLoaded
        },
        pruneYAxis() {
            _.forEach(this.yAxis, (axis, index) => {
                // If there are not series on the axis remove it from yAxis
                if (axis.series.length === 0) {
                    _.pull(this.chart.yAxis, axis.am4Axis)
                    _.pull(this.yAxis, axis)
                }
            })
        },
        /* This is a graph with a lot of data points
         * 15,000 readings translates to:
         * interval             |  Time on Graph
         * 15 minute check-in   | 5.2 Months
         * 5 minute check-in    | 1.9 months
         * 1 minute check-in    | 10.4 Days
         * 1 Second Check-in    | 4.2 Hours
         */
        isHeavyLoad() {
            // This is a heavy load if there are 15,000 or more points
            if (this.numData > 15000) {
                return true
            }

            // Count how many series are rendered
            let numSeries = 0
            _.forEach(this.yAxis, axis => {
                numSeries += axis.series.length
            })

            // This is a heavy load if there are more than 1 series
            return numSeries > 1
        },
        showQaTestData() {
            return this.environment.isQa() || this.environment.isLocal() || this.environment.isStaging()
        },
        showBackFillToggle() {
            let shouldBackFill = false;
            _.each(this.seriesList, s => {
                if (s.isVisible() && s.hasBackFillIntervals()) {
                    shouldBackFill = true
                }
            })

            return shouldBackFill
        },
    },
    watch: {
        useShortGaps() {
            this.updateChart()
        },
        useUTC() {
            if (this.useUTC) {
                this.$emit('update:timezone', 'UTC')
            }
            else {
                this.$emit('update:timezone', this.companyTimezone)
            }
            this.updateChart()
        },
        showPairedPoint() {
            if (this.showPairedPoint && ! _.isNil(this.pairedPoints)) {
                // Add each paired point to the graph.
                _.each(this.pairedPoints, point => {
                    this.seriesToShow.push(`${point.ID}`)
                })
            }
            // If we should only show this point (i.e. showPaired is false && show series modal is false)
            else if (! this.showPairedPoint && ! this.showSeriesModal) {
                // Remove Series From Graph
                _.remove(this.seriesToShow, value => {
                    // Since we should return to the display mode of 'this point' only, we should remove all other points
                    return `${value}` !== `${this.id}`
                })
            }
            this.updateChart()
        },
        validationReadings() {
            console.log(`[MeterGraphFull] validationReadings watcher triggered:`, this.validationReadings)
            console.log(`[MeterGraphFull] Number of series in seriesList:`, Object.keys(this.seriesList).length)
            _.each(this.seriesList, s => {
                console.log(`[MeterGraphFull] Updating validation readings for series ${s.id}`)
                s.setValidationReadings(this.validationReadings)
            })
            this.updateChart()
        },
        timezone() {
            if (this.timezone !== 'UTC') {
                this.companyTimezone = this.timezone
            }
            this.dateAxis.setTimezone(this.timezone)
            this.updateChart()
        },
        filterDataReady(val, old) {
            if (this.filterDataReady && !old) {
                this.updateChart()
            }
        },
        chartReady(val, old) {
            if (old === false) {
                // Temporary Series Load Command
                let series = this.seriesList[this.id]
                if (!_.isNil(series)) {
                    series.setupSeries()
                    series.setupThresholds()
                    series.loadData(this.startDate, this.endDate)
                    this.loadingPageStatus = this.loadingStatus.data
                }
            }
        },
        seriesToShow: {
            handler(val, old) {
                _.each(this.seriesList, (series, key) => {
                    if (_.indexOf(this.seriesToShow, series.id) === -1 && series.id !== this.id) {
                        // If the series is not in the list, remove it from the chart
                        _.each(this.yAxis, axis => {
                            axis.removeSeries(series)
                        })
                    }
                    else if (! this.seriesList[key].isVisible()) {
                        // The series should be rendered, but only add it to the chart if it is not already visible
                        this.addSeriesToChart(this.seriesList[key])
                    }
                })

                // If all but the first series are removed, render the thresholds again
                let leftAxis = this.yAxis[0]
                if (this.seriesToShow.length === 1) {
                    _.each(leftAxis.series, series => {
                        series.setupThresholds()
                    })
                }
                else {
                    // If there are multiple series to show, remove the thresholds
                    _.each(leftAxis.series, series => {
                        series.clearThresholds()
                    })
                }

                _.each(this.yAxis, axis => {
                    axis.updateMinMax()
                    axis.renderAxis()
                })

                this.setupScrollbar()
                this.setupLegend()
            },
            deep: true
        },
        showSeriesModal(val, old) {
            if (val !== old) {
                this.selectingSeries = this.showSeriesModal
            }
        },
        selectingSeries(val, old) {
            if (val !== old) {
                this.$emit('update:showSeriesModal', this.selectingSeries)
            }

        },
        pointIds(val, old) {
            if (val !== old) {
                this.loadSeriesSummaryData()
            }
        },
        loadBackFillWindows(val, old) {
            if (val === old) {
                return
            }

            // Update Series with back-fill window setting
            _.each(this.seriesList, series => {
                series.updateLoadingBackFillIntervals(this.loadBackFillWindows)
            })

            // Render Back-fill Windows for visible series
            _.each(this.seriesList, series => {
                if (series.visible) {
                    series.renderBackFillIntervals()
                }
            })


            // Update Chart?
            // this.updateChart()
        },
    },
    computed: {
        totalNumberOfPoints() {
            return this.numData()
        },
        graphHeight(){
            let clientHeight = document.documentElement.clientHeight;
            clientHeight = Math.max(clientHeight - 400, 450);
            return "height: " + clientHeight.toString() + "px;"
        },
        prettyLastUpdateTime() {
            if (!_.isNil(this.lastUpdateTime)) {
                let time = new Date(this.lastUpdateTime)
                return `On: ${time.toLocaleString("en-US", {timeZone: this.timezone})}`
            }
            else {
                return 'Never'
            }
        },
        hasPairedPoint() {
            return !_.isNil(this.pairPointOptions) && Object.keys(this.pairPointOptions).length > 0
        },
        pairPointSelected() {
            return this.showPairedPoint && !_.isNil(this.pairedPoints)
        },
    },
    beforeUnmount() {
        if (!_.isNil(this.chart)) {
            this.chart.dispose()
        }
        this.chart = null
    }
}

</script>

<style scoped>
    #meter-graph {
        height: 40em
    }
    .dark-theme {
        background-color: #30303d;
        color: #fff;
    }
</style>